import { Controller } from "@hotwired/stimulus"
import { ArcadeStage } from "../utils/arcade_stage"

// Tape-taupe : des espaces sortent de leur trou, certains en impayé. On ne tape que les
// impayés ; taper un espace en règle coûte une vie.
//
// Laisser filer un impayé coûte une vie, comme taper un espace en règle : sans ça, une
// partie où l'on ne touche à rien ne se termine jamais. C'est ce qui oblige à jouer.
//
// Les montants sont RÉELS : ils viennent du grand livre, agrégés par société sur les
// contrats que le joueur a le droit de voir (cf. Arcade::MoleField). Le score n'est donc pas
// un nombre de coups mais la somme recouvrée, ce qui rend chaque taupe inégale : deux
// impayés à 30 € ne valent pas celui à 4 000 €, et on apprend à viser.
const HOLES = 9
const LIVES = 3
// Le rythme se resserre à CHAQUE sortie, pas seulement quand on tape juste : indexée sur les
// coups réussis, l'accélération récompensait le bon joueur en punissant le mauvais d'une
// partie interminable. Au fil des sorties, la taupe passe de 1,5 s à une demi-seconde, et
// l'entre-deux se resserre dans la même proportion.
const START_UP_MS = 1500
const MIN_UP_MS = 520
const SPEED_STEP = 38
const START_GAP_MS = 340
const MIN_GAP_MS = 150

export default class extends Controller {
  static values = { labels: Object, url: String, scoresUrl: String, scoreUnit: String }

  start() {
    this.stage = new ArcadeStage({
      labels: this.labelsValue,
      scoresUrl: this.scoresUrlValue,
      scoreUnit: this.scoreUnitValue,
      onRestart: () => this.#play(),
      onClose: () => this.#stop(),
    })
    this.stage.open()
    this.field = document.createElement("div")
    this.field.className = "arcade-moles"
    this.stage.board.appendChild(this.field)
    this.#play()
  }

  #stop() {
    window.clearTimeout(this.timer)
    this.request?.abort()
    this.request = null
    this.field = null
  }

  async #play() {
    this.#message(this.labelsValue.loading || "…")

    this.request?.abort()
    this.request = new AbortController()

    // Le `try` ne couvre QUE la requête : une erreur de rendu affichée comme une panne
    // réseau enverrait chercher un bug là où il n'est pas.
    let targets
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        signal: this.request.signal,
      })
      if (!response.ok) throw new Error(response.statusText)

      targets = (await response.json()).targets
    } catch (error) {
      if (error.name === "AbortError") return
      this.#message(this.labelsValue.error || "")
      return
    }

    if (!targets?.length) return this.#message(this.labelsValue.empty || this.labelsValue.error)

    this.queue = targets
    this.#begin()
  }

  #message(text) {
    if (!this.field) return
    this.field.className = "arcade-moles arcade-moles--message"
    this.field.replaceChildren()
    const node = document.createElement("p")
    node.className = "arcade-quiz__message"
    node.textContent = text
    this.field.appendChild(node)
  }

  #begin() {
    this.field.className = "arcade-moles"
    this.field.replaceChildren()
    this.holes = Array.from({ length: HOLES }, () => this.#hole())

    this.index = 0
    this.over = false
    this.stage.setScore(0)
    this.#setLives(LIVES)
    this.stage.setHint(this.labelsValue.hint || "")
    this.#pop()
  }

  // Un trou : le monticule de terre, et la taupe qui vit dessous. `overflow: hidden` fait le
  // reste — elle SORT du sol au lieu d'apparaître.
  #hole() {
    const hole = document.createElement("button")
    hole.type = "button"
    hole.className = "arcade-moles__hole"

    const mole = document.createElement("span")
    mole.className = "arcade-moles__mole"
    mole.innerHTML =
      '<span class="arcade-moles__ear arcade-moles__ear--left"></span>' +
      '<span class="arcade-moles__ear arcade-moles__ear--right"></span>' +
      '<span class="arcade-moles__face"></span>' +
      '<span class="arcade-moles__snout"></span>'

    const label = document.createElement("span")
    label.className = "arcade-moles__label"

    const dirt = document.createElement("span")
    dirt.className = "arcade-moles__dirt"

    hole.append(mole, label, dirt)
    hole.addEventListener("click", () => this.#whack(hole))
    this.field.appendChild(hole)
    return hole
  }

  #pop() {
    if (this.over || !this.field) return

    const target = this.queue[this.index % this.queue.length]
    this.index += 1

    this.active = this.holes[Math.floor(Math.random() * this.holes.length)]
    this.activeTarget = target

    this.active.querySelector(".arcade-moles__label").textContent = target.title
    this.#dressFace(this.active.querySelector(".arcade-moles__face"), target)
    this.active.classList.add("arcade-moles__hole--up")
    this.active.classList.toggle("arcade-moles__hole--arrears", target.arrears)
    this.active.setAttribute("aria-label", target.title)

    this.timer = window.setTimeout(() => this.#timeUp(), this.#upMs())
  }

  // Le temps passé dehors, et l'entre-deux qui le suit : les deux se resserrent ensemble,
  // sinon la partie accélère sans jamais devenir plus dense.
  #upMs() {
    return Math.max(MIN_UP_MS, START_UP_MS - (this.index - 1) * SPEED_STEP)
  }

  #gapMs() {
    return Math.max(MIN_GAP_MS, Math.round(START_GAP_MS * (this.#upMs() / START_UP_MS)))
  }

  // Le museau de la taupe, c'est le logo de l'espace. À défaut, ses initiales — un trou vide
  // ne dit pas sur qui on tape.
  #dressFace(face, target) {
    face.replaceChildren()
    if (!target.logo_url) {
      face.textContent = initialsOf(target.title)
      return
    }

    const image = new Image()
    image.alt = ""
    image.className = "arcade-moles__logo"
    image.addEventListener("error", () => { image.remove(); face.textContent = initialsOf(target.title) }, { once: true })
    face.appendChild(image)
    image.src = target.logo_url
  }

  // La taupe est rentrée toute seule. Si c'était un impayé, il vient de filer : une vie.
  #timeUp() {
    if (!this.active) return

    const missed = this.activeTarget?.arrears
    if (missed) this.#showMiss(this.active)

    this.#clearActive()
    if (missed && !this.#loseLife()) return

    this.timer = window.setTimeout(() => this.#pop(), this.#gapMs())
  }

  // La taupe rentre après un coup : on enchaîne, la vie a déjà été comptée par #whack.
  #hide() {
    if (!this.active) return

    this.#clearActive()
    this.timer = window.setTimeout(() => this.#pop(), this.#gapMs())
  }

  #clearActive() {
    if (!this.active) return

    this.active.classList.remove("arcade-moles__hole--up", "arcade-moles__hole--arrears", "arcade-moles__hole--hit")
    this.active.removeAttribute("aria-label")
    this.active = null
    this.activeTarget = null
  }

  #whack(hole) {
    if (this.over || hole !== this.active) return

    window.clearTimeout(this.timer)
    hole.classList.add("arcade-moles__hole--hit")

    if (this.activeTarget.arrears) {
      const euros = Math.round((this.activeTarget.cents || 0) / 100)
      this.#showGain(hole, euros)
      this.stage.setScore(this.stage.score + euros)
      this.#hide()
      return
    }

    // Un espace en règle : la main part trop vite, ça coûte une vie.
    if (!this.#loseLife()) return this.#clearActive()

    this.#hide()
  }

  // Ce qu'on vient de laisser filer, en rouge et barré d'un « − » : sans ce repère, une vie
  // qui disparaît pendant qu'on regarde ailleurs passe pour un bug.
  #showMiss(hole) {
    const miss = document.createElement("span")
    miss.className = "arcade-moles__gain arcade-moles__gain--miss"
    miss.textContent = `−${this.stage.formatScore(Math.round((this.activeTarget.cents || 0) / 100))}`
    hole.appendChild(miss)
    window.setTimeout(() => miss.remove(), 900)
  }

  // « + 145 € » qui monte et s'efface : c'est ce qui fait comprendre d'un coup que toutes les
  // taupes ne se valent pas.
  #showGain(hole, euros) {
    const gain = document.createElement("span")
    gain.className = "arcade-moles__gain"
    gain.textContent = `+${this.stage.formatScore(euros)}`
    hole.appendChild(gain)
    window.setTimeout(() => gain.remove(), 900)
  }

  #setLives(value) {
    this.lives = value
    this.stage.setLives(value, LIVES)
  }

  // Renvoie `false` quand la partie s'arrête, pour que l'appelant n'enchaîne pas sur une
  // nouvelle taupe.
  #loseLife() {
    this.#setLives(this.lives - 1)
    if (this.lives > 0) return true

    this.over = true
    window.clearTimeout(this.timer)
    this.stage.finish({ score: this.stage.score, message: this.labelsValue.busted || "" })
    return false
  }
}

const initialsOf = (title) =>
  title
    .split(/[\s\-']+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("")
